import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Sparkles, Send, RotateCcw } from 'lucide-react';

const PRESETS = [
  {
    tag: 'AI 導師',
    script: [
      {
        role: 'hook',
        label: 'HOOK',
        text: '你會什麼，已經不值錢了。\n不是因為 AI 比你厲害，是「會什麼」這個公式本身，已經死了。',
      },
      {
        role: 'story',
        label: 'STORY',
        text: '我一個朋友，做了十五年設計。上個月收到客戶一條訊息：「不好意思，我找了個新人，他用 AI 做，三天交稿，報價只有你一半。」\n\n他呆了一下，回：「他做得出我那種質感嗎？」客戶想了很久，回了一句：「老實說……差不多。」\n\n他打給我只說一句話：「做了十五年，一夜之間不值錢了。」',
      },
      {
        role: 'body',
        label: 'FRAMEWORK',
        text: '我跟他說，你搞錯了一件事。讓你不值錢的不是 AI，是你還在用「我會什麼」來定價。\n\n以前你值多少錢，看你會什麼、做了多久，這兩樣加起來就是你的身價。這個公式撐了一百年。但 AI 一出來它整個崩掉——不是因為 AI 把你取代，是因為 AI 讓「會」這個字本身變得沒意義。\n\n那現在按什麼定價？\n\n你自己想一下，提到某個領域，你腦子裡會冒出哪幾個名字？那幾個人，專業不一定最強，作品也不一定最好，但生意都往他們那跑，報價還特別貴。\n\n這就是新的定價單位——\n不是你技能多稀缺，是你在多少人腦子裡，佔了一個位置。\n\n在你做的那個領域，列 10 個可能的客戶，問自己：提到這件事，有幾個人會第一個想到你？\n\n0 個 → 不管你多專業，你的定價都在崩塌\n1–2 個 → 有位置，但那個位置很小\n10 個以上 → AI 再強，動不到你',
      },
      {
        role: 'cta',
        label: 'CTA',
        text: '那個十五年的設計師後來問：「我這十五年——白做了？」\n\n我說：不是白做。是你這十五年只做了一半。你練到了「會」，但沒練到「被想起」。前面一半，AI 一年就追平；後面一半，它一百年都追不上。\n\n未來值錢的人，不會是會最多東西的那個。\n是最多人想起的那個。',
      },
    ],
  },
  {
    tag: '理財顧問',
    script: [
      { role: 'hook', label: 'HOOK', text: '賺越多錢，選擇越少。' },
      {
        role: 'story',
        label: 'STORY',
        text: '我有個律師朋友，月入三十萬。上個月他太太生第二胎，他想請兩個星期假陪月。他拿出行事曆，發現一格都空不出來。\n\n客戶等他簽文件，團隊等他開會，兩棟樓的房貸等他供。他坐在辦公室跟我說：「我做了十二年律師，建了一個離不開我的事業——其實是它擁有了我。」\n\n同一個星期，我另一個朋友，收入只有他三分之一，直接關手機，消失兩星期，去冰島看極光。\n\n這兩個人的差別不是錢，是一樣更根本的東西——「選擇權」。',
      },
      {
        role: 'body',
        label: 'FRAMEWORK',
        text: '大部分人一輩子追的那個叫「事業」，它其實只是手段，真正的目的是選擇權。我們拼到一半把手段當成目的，結果事業越大、選擇權越少。\n\n你能不能明天跟最大客戶說「不」？能不能下個月消失兩個星期？能不能突然換一條完全不同的跑道？\n\n如果全部都不行，你建的不是事業，是一個需要你永遠在場的東西。\n\n差別不在於你多努力，在於結構。你擁有它，還是它擁有你？\n\n接一個大單，綁你三年 → 選擇權減少\n請一個能獨當一面的人 → 選擇權增加\n收入全押在一個客戶身上 → 選擇權減少\n做一件你睡著還在賺錢的事 → 選擇權增加',
      },
      {
        role: 'cta',
        label: 'CTA',
        text: '那個律師朋友最後跟我說：「我以為我在為自由工作，回頭看才發現，這十二年，每一步都在把自己關得更緊。」\n\n事業做大，不是成功。能隨時離開它——才是。',
      },
    ],
  },
];

const STREAM_SPEED = 12; // ms per character

const SEG_STYLES = {
  hook:  { color: 'text-lime-500',    weight: 'font-semibold', size: 'text-base' },
  story: { color: 'text-white/95',    weight: 'font-medium',   size: 'text-sm'  },
  body:  { color: 'text-white',       weight: 'font-medium',   size: 'text-sm'  },
  cta:   { color: 'text-lime-300',    weight: 'font-medium',   size: 'text-sm'  },
};

const InteractiveDemo = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [active, setActive] = useState(null); // preset index
  const [streaming, setStreaming] = useState(false);
  const [segments, setSegments] = useState([]); // revealed segments w/ partial text
  const timers = useRef([]);
  const scrollRef = useRef(null);

  // Auto-scroll the phone mockup while streaming
  useEffect(() => {
    if (scrollRef.current && streaming) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [segments, streaming]);

  const reset = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
    setActive(null);
    setStreaming(false);
    setSegments([]);
  };

  const start = (idx) => {
    reset();
    setActive(idx);
    setStreaming(true);
    const script = PRESETS[idx].script;
    let elapsed = 0;
    script.forEach((seg, si) => {
      // add placeholder
      timers.current.push(
        setTimeout(() => {
          setSegments((prev) => [...prev, { ...seg, text: '' }]);
        }, elapsed)
      );
      // stream characters
      [...seg.text].forEach((ch, ci) => {
        elapsed += STREAM_SPEED;
        timers.current.push(
          setTimeout(() => {
            setSegments((prev) => {
              const copy = [...prev];
              copy[si] = { ...copy[si], text: (copy[si]?.text || '') + ch };
              return copy;
            });
          }, elapsed)
        );
      });
      elapsed += 250; // gap
    });
    // finished
    timers.current.push(
      setTimeout(() => setStreaming(false), elapsed + 100)
    );
  };

  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);

  const segStyle = (role) => SEG_STYLES[role] || SEG_STYLES.body;

  return (
    <section
      ref={ref}
      id="demo"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="interactive-demo-section"
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% 20%, rgba(255,84,54,0.07), transparent 60%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-12 md:mb-16"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-lime-500/60" />
            <span className="label-micro text-lime-500">LIVE · TRY</span>
          </div>
          <h2 className="font-display font-semibold text-white tracking-tighter text-4xl md:text-5xl lg:text-6xl mb-5 leading-[1.05]">
            不用註冊，<span className="text-lime-500">現在就試</span>
          </h2>
          <p className="text-obsidian-200 text-base md:text-lg max-w-xl">
            選一個你的創作領域，看 AI 如何在 3 秒內生成一支完整的 Reels 腳本。
          </p>
        </motion.div>

        <div className="grid md:grid-cols-[1fr_420px] gap-6 md:gap-10 items-start">
          {/* LEFT: Prompt panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative bg-obsidian-900 border border-white/[0.08] rounded-2xl p-6 md:p-8 overflow-hidden"
          >
            {/* Grid texture */}
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />

            <div className="relative">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles className="w-4 h-4 text-lime-500" strokeWidth={2} />
                <span className="label-micro text-obsidian-300">STEP · 01 · 選擇創作領域</span>
              </div>

              <div className="flex flex-wrap gap-2.5 mb-8">
                {PRESETS.map((p, i) => (
                  <button
                    key={p.tag}
                    onClick={() => start(i)}
                    disabled={streaming}
                    data-testid={`preset-chip-${i}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                      active === i
                        ? 'bg-lime-500 text-obsidian-900 border-lime-500 shadow-[0_0_20px_rgba(255,84,54,0.35)]'
                        : 'bg-white/[0.03] text-obsidian-100 border-white/10 hover:border-lime-500/40 hover:text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {p.tag}
                  </button>
                ))}
              </div>

              {/* Prompt input look */}
              <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
                <div className="flex-1 text-sm text-obsidian-200 truncate">
                  {active !== null
                    ? `為「${PRESETS[active].tag}」生成一支 30 秒爆款 Reels`
                    : '請選擇一個領域開始 →'}
                </div>
                <button
                  onClick={() => active !== null && !streaming && start(active)}
                  disabled={active === null || streaming}
                  className="w-9 h-9 rounded-lg bg-lime-500 text-obsidian-900 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 transition-transform"
                  data-testid="demo-send-btn"
                >
                  {streaming ? (
                    <div className="w-3 h-3 bg-obsidian-900 rounded-sm animate-pulse" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>

              {active !== null && (
                <button
                  onClick={reset}
                  className="mt-4 inline-flex items-center gap-1.5 label-micro text-obsidian-400 hover:text-lime-500 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> RESET
                </button>
              )}
            </div>
          </motion.div>

          {/* RIGHT: Phone mockup output */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative mx-auto"
          >
            <div
              className="relative bg-obsidian-900 border border-white/10 rounded-[32px] p-3 shadow-2xl"
              style={{ width: 320, aspectRatio: '9/16' }}
            >
              {/* Notch */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full" />

              <div className="h-full bg-gradient-to-b from-obsidian-800 to-obsidian-900 rounded-[24px] overflow-hidden relative">
                {/* Background gradient accent */}
                <div
                  className="absolute inset-0 opacity-40"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 20%, rgba(255,84,54,0.12), transparent 60%)',
                  }}
                />

                <div className="relative h-full flex flex-col p-5 pt-10">
                  <div className="label-micro text-obsidian-400 mb-4 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-lime-500 animate-pulse" />
                    REELSCRIPT · LIVE
                  </div>

                  {segments.length === 0 && (
                    <div className="flex-1 flex items-center justify-center text-center">
                      <div>
                        <div className="w-10 h-10 mx-auto mb-3 rounded-full border border-white/20 flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-obsidian-300" strokeWidth={1.5} />
                        </div>
                        <p className="text-xs text-obsidian-400 leading-relaxed">
                          選擇一個創作領域<br />AI 會在此生成腳本
                        </p>
                      </div>
                    </div>
                  )}

                  <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-1 no-scrollbar scroll-smooth">
                    {segments.map((seg, i) => {
                      const s = segStyle(seg.role);
                      return (
                        <div key={i} className="space-y-1.5">
                          <div className="label-micro text-obsidian-400 flex items-center gap-1.5">
                            <span className="w-4 h-px bg-lime-500/60" />
                            {seg.label}
                          </div>
                          <p
                            className={`${s.size} ${s.color} ${s.weight} leading-relaxed whitespace-pre-line`}
                          >
                            {seg.text}
                            {streaming && i === segments.length - 1 && (
                              <span className="inline-block w-1 h-3.5 bg-lime-500 ml-0.5 align-middle animate-pulse" />
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {!streaming && segments.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between"
                    >
                      <span className="label-micro text-lime-500">✓ 生成完畢</span>
                      <span className="label-micro text-obsidian-400">
                        {segments.length} 段 · {segments.reduce((acc, s) => acc + (s.text?.length || 0), 0)} 字
                      </span>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Floating hint */}
            <div className="absolute -right-2 top-10 bg-obsidian-900 border border-lime-500/30 rounded-lg px-3 py-1.5 text-[10px] text-lime-500 font-medium rotate-3 shadow-[0_0_20px_rgba(255,84,54,0.15)] hidden md:block">
              ← 即時串流
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemo;
